import React from "react";
import { ArrowUpDown } from "lucide-react";
import { Popover } from "antd";
import { AppTooltip } from "../../Tooltip/AppTooltip";
import { Icon } from "../../Icon/Icon";
import type { OpenDropdownState, SortOrder, TableSortState } from "../types";
import { FilterButton, HeaderCellWrapper, HeaderTitle, IconContainer, SortButton, SortIconSet } from "../styles";
import { TableHeaderDropdown } from "./TableHeaderDropdown";

type CustomHeaderCellProps = {
	title: string;
	columnKey: string;
	enableSort: boolean;
	enableFilter: boolean;
	sortState: TableSortState | null;
	openDropdown: OpenDropdownState;
	setOpenDropdown: (state: OpenDropdownState) => void;
	filterOptions: string[];
	selectedFilters: string[];
	filterSearch: string;
	onSortSelect: (columnKey: string, order: SortOrder | null) => void;
	onFilterSearchChange: (columnKey: string, value: string) => void;
	onFilterToggle: (columnKey: string, value: string) => void;
	sortLabels: { ascend: string; descend: string };
};

export const CustomHeaderCell: React.FC<CustomHeaderCellProps> = ({
	title,
	columnKey,
	enableSort,
	enableFilter,
	sortState,
	openDropdown,
	setOpenDropdown,
	filterOptions,
	selectedFilters,
	filterSearch,
	onSortSelect,
	onFilterSearchChange,
	onFilterToggle,
	sortLabels,
}) => {
	const isFilterActive = selectedFilters.length > 0;
	const isSortActive = sortState?.columnKey === columnKey;
	const isDropdownOpenForColumn = openDropdown?.columnKey === columnKey;
	const isActive = isFilterActive || isSortActive || isDropdownOpenForColumn;

	return (
		<HeaderCellWrapper>
			<HeaderTitle>{title}</HeaderTitle>
			<IconContainer className="header-icons" $active={isActive}>
				{enableFilter && (
					<Popover
						trigger="click"
						open={openDropdown?.columnKey === columnKey && openDropdown.type === "filter"}
						onOpenChange={(open) => {
							setOpenDropdown(open ? { columnKey, type: "filter" } : null);
						}}
						mouseEnterDelay={0}
						mouseLeaveDelay={0}
						content={
							<TableHeaderDropdown
								type="filter"
								columnKey={columnKey}
								sortState={sortState}
								filterOptions={filterOptions}
								selectedFilters={selectedFilters}
								filterSearch={filterSearch}
								onSortSelect={onSortSelect}
								onFilterSearchChange={onFilterSearchChange}
								onFilterToggle={onFilterToggle}
							/>
						}
						placement="bottomLeft"
						overlayInnerStyle={{ padding: 0, borderRadius: 14 }}
					>
						<AppTooltip
							title="Filter"
							placement="bottom"
							arrow={false}
							backgroundColor="rgba(50,50,50,0.85)"
							mouseEnterDelay={0}
						>
							<FilterButton $active={isFilterActive} onClick={(e) => e.stopPropagation()}>
								<Icon src="/icon/table-filter.svg" size={16} />
							</FilterButton>
						</AppTooltip>
					</Popover>
				)}

				{enableSort && (
					<Popover
						trigger="click"
						open={openDropdown?.columnKey === columnKey && openDropdown.type === "sort"}
						onOpenChange={(open) => {
							setOpenDropdown(open ? { columnKey, type: "sort" } : null);
						}}
						mouseEnterDelay={0}
						mouseLeaveDelay={0}
						content={
							<TableHeaderDropdown
								type="sort"
								columnKey={columnKey}
								sortState={sortState}
								filterOptions={[]}
								selectedFilters={[]}
								filterSearch=""
								onSortSelect={onSortSelect}
								onFilterSearchChange={onFilterSearchChange}
								onFilterToggle={onFilterToggle}
								sortLabels={sortLabels}
							/>
						}
						placement="bottomLeft"
						overlayInnerStyle={{ padding: 0, borderRadius: 14 }}
					>
						<AppTooltip
							title="Sort"
							placement="bottom"
							arrow={false}
							backgroundColor="rgba(50,50,50,0.85)"
							mouseEnterDelay={0}
						>
							<SortButton $active={isSortActive} onClick={(e) => e.stopPropagation()}>
								<SortIconSet>
									<ArrowUpDown size={16} strokeWidth={2.5} />
								</SortIconSet>
							</SortButton>
						</AppTooltip>
					</Popover>
				)}
			</IconContainer>
		</HeaderCellWrapper>
	);
};
